import React, { useEffect, useState, useRef } from "react";
import { Spinner } from "reactstrap";
import { toast } from "react-toastify";
import { useUserContext } from "libs/context/UserContext";
import { Offcanvas } from "bootstrap";
import CandidateTrackApplication from "pages/CandidateTrackApplication/candidate-track-application";
import { candidateOnboardingDoucmentsList, candidateOnboardingDocumentUpload, candidateUpdateCitigen } from "model/CandidateDetailsModel";

const CandidateOnboardingSection = () => {
    const { candidateDetailsData, fetchCandidateDetailsDataForUpdate, loadingDetailsForUpdate } = useUserContext();
    const driverDetails = candidateDetailsData?.DriverDetails || {};

    const [documentEdit, setDocumentEdit] = useState(true);
    const [submitLoading, setSubmitLoading] = useState(false);
    const InterestedOrNot = localStorage?.getItem("InterestedOrNot")
    const [InterestedValue, setInterested] = useState(false);

    const [documentsForm, setDocumentsForm] = useState({});
    const [errors, setErrors] = useState({});
    const [onboardingList, setOnboardingList] = useState();
    const [loading, setLoading] = useState(false);
    const [showCitizenModal, setShowCitizenModal] = useState(false);
    const [anythingChange, setAnythingChange] = useState(false);



    const offcanvasRef = useRef(null);
    const offcanvasInstanceRef = useRef(null);
    useEffect(() => {
        if (offcanvasRef.current) {
            offcanvasInstanceRef.current = new Offcanvas(offcanvasRef.current, {
                backdrop: true,
                scroll: true,
                keyboard: true
            });
        }
    }, []);


    const openOffcanvas = () => {
        offcanvasInstanceRef.current?.show();
    };

    const validateDocuments = () => {
        const docs = onboardingList?.Documents || [];
        const nextErrors = {};

        docs.forEach((doc) => {
            const docKey = String(doc.OnboardingDocumentId);
            const localDoc = documentsForm?.[docKey] || {};
            const hasNumberInput = Boolean((localDoc?.DocumentNumber ?? "").trim());
            const hasExistingNumber = Boolean(
                (doc?.DocumentNumber ?? doc?.PrefilledDocumentNumber ?? "").trim()
            );
            const hasFileInput = Boolean(localDoc?.File);
            const hasExistingFile =
                typeof doc?.UploadUrl === "string" && doc.UploadUrl.trim().length > 0;
            const hasAvailableNumber = hasNumberInput || hasExistingNumber;
            const hasAvailableFile = hasFileInput || hasExistingFile;

            // If both current inputs are blank, pass with no error.
            if (!hasNumberInput && !hasFileInput) {
                return;
            }

            // Pair validation with available values (existing + current):
            // - number exists but file doesn't => file error
            // - file exists but number doesn't => number error
            // - both exist OR both missing => no error
            if (hasFileInput && !hasNumberInput) {
                nextErrors[`${docKey}-number`] = `${doc.DocumentName} number is required`;
            } else if (hasNumberInput && !hasAvailableFile) {
                nextErrors[`${docKey}-file`] = `${doc.DocumentName} file is required`;
            } else if (hasAvailableNumber && !hasAvailableFile) {
                nextErrors[`${docKey}-file`] = `${doc.DocumentName} file is required`;
            } else if (hasAvailableFile && !hasAvailableNumber) {
                nextErrors[`${docKey}-number`] = `${doc.DocumentName} number is required`;
            }
        });

        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const hasAnyDocumentNumberChanged = (nextForm) => {
        const docs = onboardingList?.Documents || [];
        return docs.some((doc) => {
            const key = String(doc.OnboardingDocumentId);
            const originalNumber = (doc?.DocumentNumber ?? doc?.PrefilledDocumentNumber ?? "").trim();
            const currentNumber = (nextForm?.[key]?.DocumentNumber ?? "").trim();
            return currentNumber !== originalNumber;
        });
    };

    const handleDocumentNumberChange = (docId, value) => {
        const key = String(docId);
        setDocumentsForm((prev) => {
            const next = {
                ...prev,
                [key]: {
                    ...(prev[key] || {}),
                    DocumentNumber: value,
                },
            };

            setAnythingChange(hasAnyDocumentNumberChanged(next));
            return next;
        });

        setErrors((prev) => {
            const next = { ...prev };
            delete next[`${key}-number`];
            return next;
        });
    };

    const handleDocumentFileChange = (docId, file) => {
        const key = String(docId);
        setDocumentsForm((prev) => ({
            ...prev,
            [key]: {
                ...(prev[key] || {}),
                File: file || null,
            },
        }));
        setAnythingChange(true)
        setErrors((prev) => {
            const next = { ...prev };
            delete next[`${key}-file`];
            return next;
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitLoading(true);

        try {
            const isValid = validateDocuments();
            if (!isValid) return;

            const docs = onboardingList?.Documents || [];
            const docsToUpload = docs
                .map((doc) => {
                    const key = String(doc.OnboardingDocumentId);
                    const localDoc = documentsForm[key] || {};
                    const existingNumber = (doc.DocumentNumber ?? doc.PrefilledDocumentNumber ?? "").trim();
                    const currentNumber = (localDoc.DocumentNumber ?? existingNumber).trim();
                    const hasUploadUrl = Boolean(
                        typeof doc?.UploadUrl === "string" && doc.UploadUrl.trim()
                    );
                    const numberChanged = currentNumber !== existingNumber;

                    return {
                        OnboardingDocumentId: doc.OnboardingDocumentId,
                        DocumentNumber: currentNumber,
                        DocumentFile: localDoc.File || null,
                        hasUploadUrl,
                        numberChanged,
                    };
                })
                .filter((doc) => doc.DocumentFile || (doc.hasUploadUrl && doc.numberChanged));

            // if (!docsToUpload.length) {
            //     toast.error("Please choose at least one document file to upload.");
            //     return;
            // }

            const formData = new FormData();
            docsToUpload.forEach((doc, index) => {
                formData.append(`Items[${index}].OnboardingDocumentId`, doc.OnboardingDocumentId);
                formData.append(`Items[${index}].DocumentNumber`, doc.DocumentNumber || "");
                if (doc.DocumentFile) {
                    formData.append(`Items[${index}].DocumentFile`, doc.DocumentFile);
                } else if (doc.hasUploadUrl) {
                    formData.append(`Items[${index}].DocumentFile`, null);
                }
                else {
                    formData.append(`Items[${index}].DocumentFile`, null);
                }
            });

            const response = await candidateOnboardingDocumentUpload(formData);
            if (!response?.IsSuccess) {
                toast.error(response?.Message || "Document upload failed");
                return;
            }

            toast.success(response?.Message || "Documents uploaded successfully");
            fetchCandidateDetailsDataForUpdate();
            fetchData();
            setAnythingChange(false);
        } catch (err) {
            toast.error(err?.message || "Something went wrong");
        } finally {
            setSubmitLoading(false);
        }
    };


    const cancelForm = () => setDocumentEdit(true);

    useEffect(() => {
        const docs = onboardingList?.Documents || [];
        const initialForm = {};

        docs.forEach((doc) => {
            initialForm[String(doc.OnboardingDocumentId)] = {
                DocumentNumber: doc?.DocumentNumber || doc?.PrefilledDocumentNumber || "",
                File: null,
            };
        });

        setDocumentsForm(initialForm);
        setErrors({});
        setAnythingChange(false);
    }, [onboardingList?.Documents]);


    useEffect(() => {
        setInterested(false)
        if (InterestedOrNot == 'Not-Interested') {
            setInterested(false)
        }
        else if (InterestedOrNot == 'Interested') {
            setInterested(true)
        }
    }, [InterestedOrNot]);


    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        setShowCitizenModal(onboardingList?.IsUSCitizen === -1);
    }, [onboardingList?.IsUSCitizen]);

    const handleCitizenSelection = (citizenValue) => {
        setOnboardingList((prev) => ({ ...(prev || {}), IsUSCitizen: citizenValue }));
        setShowCitizenModal(false);
        updateCitigen(citizenValue)
    };

    const fetchData = async () => {
        try {
            setLoading(true);
            const onboardingDocData = await candidateOnboardingDoucmentsList([]);
            if (!onboardingDocData?.IsSuccess) return;

            const onboardingList = onboardingDocData?.Data;
            setOnboardingList(onboardingList);
        } catch (err) {
            toast.error(err?.message || "Something went wrong");
        } finally {
            setLoading(false);
        }
    };

    const updateCitigen = async (citizenValue) => {
        try {
            setLoading(true);
            const updateCitigen = await candidateUpdateCitigen({ IsUSCitizen: citizenValue });
            if (!updateCitigen?.IsSuccess) return;
            toast.success(updateCitigen?.Message);
            fetchData()
        } catch (err) {
            toast.error(err?.message || "Something went wrong");
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            {showCitizenModal && (
                <div className="citizenship-modal-backdrop">
                    <div className="citizenship-modal-card" role="dialog" aria-modal="true" aria-labelledby="citizenship-modal-title">
                        <div className="citizenship-modal-header">
                            <h3 id="citizenship-modal-title" className="mb-0">Select Citizenship Status</h3>
                            <button
                                type="button"
                                className="citizenship-modal-close"
                                aria-label="Close"
                                onClick={() => setShowCitizenModal(false)}
                            >
                                &times;
                            </button>
                        </div>

                        <div className="citizenship-modal-content">
                            <p className="citizenship-modal-subtitle">Please select your citizenship status to continue</p>

                            <button
                                type="button"
                                className="citizenship-option-card"
                                onClick={() => handleCitizenSelection(1)}
                            >
                                <div className="citizenship-option-text">
                                    <h4>US Citizen</h4>
                                    <p>I am a citizen of the United States</p>
                                </div>
                                <span className="citizenship-option-circle" />
                            </button>

                            <button
                                type="button"
                                className="citizenship-option-card"
                                onClick={() => handleCitizenSelection(0)}
                            >
                                <div className="citizenship-option-text">
                                    <h4>Non US Citizen</h4>
                                    <p>I am not a citizen of the United States</p>
                                </div>
                                <span className="citizenship-option-circle" />
                            </button>
                        </div>

                        <div className="citizenship-modal-footer-note">
                            This information is required to proceed with the document verification process
                        </div>
                    </div>
                </div>
            )}
            <div className="active-mob-device d-xl-none">
                <button className="btn btn-primary-2 btn-callback-mob"
                    type="button"
                    data-bs-toggle="offcanvas"
                    data-bs-target="#application_status"
                    aria-controls="application_status"
                    onClick={openOffcanvas}
                >
                    Track Application
                </button>

            </div>
            {/* // } */}
            {/* </div> */}
            {(loadingDetailsForUpdate) ? (
                <div className="text-center py-5">
                    <Spinner color="primary" /> Loading...
                </div>
            ) :
                (
                    <div
                        className="tab-pane fade show active"
                        id="pills-Documents"
                        role="tabpanel"
                        aria-labelledby="pills-Documents-tab"
                    >
                        {/* View mode */}
                        {/* View mode */}
                        {!documentEdit && (
                            <>
                                <form className="row mt-4 g-3 container-padding-fix">
                                    <div className="col-12">
                                        <div className="row g-3">
                                            {/* SSN */}
                                            <div className="col-md-6 col-12">
                                                <div className="form-wrapper d-flex justify-content-between align-items-center">
                                                    <div className="d-flex flex-column">
                                                        <label className="form-label">SSN</label>
                                                        <p className="fw-bold text-dark">
                                                            {driverDetails.SSN || "NA"}
                                                        </p>
                                                    </div>
                                                    {driverDetails.SSNImageLink && (
                                                        // <div className="icon-button download-icon-theme">
                                                        <div className="icon-button col-1 mt-4 icon-button-pos-fix">
                                                            <a
                                                                href={driverDetails.SSNImageLink}
                                                                target="_blank"
                                                                rel="noreferrer"
                                                                className="btn btn-link p-0 text-white"
                                                            >
                                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                                                                    fill="currentColor" className="bi bi-download" viewBox="0 0 16 16">
                                                                    <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5" />
                                                                    <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708z" />
                                                                </svg>
                                                            </a>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Driving License */}
                                            <div className="col-md-6 col-12">
                                                <div className="form-wrapper d-flex justify-content-between align-items-center">
                                                    <div className="d-flex flex-column">
                                                        <label className="form-label">Driving License</label>
                                                        <p className="fw-bold text-dark">
                                                            {driverDetails.DLNumber || "NA"}
                                                        </p>
                                                    </div>
                                                    {driverDetails.DLImageLink && (
                                                        <div className="icon-button download-icon-theme">
                                                            <a
                                                                href={driverDetails.DLImageLink}
                                                                target="_blank"
                                                                rel="noreferrer"
                                                                className="btn btn-link p-0 text-white"
                                                            >
                                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                                                                    fill="currentColor" className="bi bi-download" viewBox="0 0 16 16">
                                                                    <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5" />
                                                                    <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708z" />
                                                                </svg>
                                                            </a>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Class / Issuing State / Expiry Date */}
                                    <div className="col-12">
                                        <div className="row g-3">
                                            <div className="col-md-4">
                                                <div className="form-wrapper d-flex flex-column">
                                                    <label className="form-label">Class</label>
                                                    <p className="fw-bold text-dark">
                                                        {driverDetails.DLClass || "NA"}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="col-md-4">
                                                <div className="form-wrapper d-flex flex-column">
                                                    <label className="form-label">Issuing State</label>
                                                    <p className="fw-bold text-dark">
                                                        {driverDetails.DLIssuingState || "NA"}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="col-md-4">
                                                <div className="form-wrapper d-flex flex-column">
                                                    <label className="form-label">Expiry Date</label>
                                                    <p className="fw-bold text-dark">
                                                        {driverDetails.DLExpiryDateAppFormat || "NA"}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </form>

                                {/* Edit button */}
                                <div className="row mt-3 container-padding-fix">
                                    <div className="col-12">
                                        <div className="d-flex justify-content-end align-items-start">
                                            <button className="btn btn-primary-2" onClick={editDocumentDetails}>
                                                Edit
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}


                        {/* Edit mode */}
                        {documentEdit && (
                            <>
                                {(onboardingList?.Documents?.length > 0) ?
                                    <form
                                        className="row mt-4 container-padding-fix"
                                        onSubmit={handleSubmit}
                                    >
                                        {(onboardingList?.Documents || []).map((doc) => {
                                            const docKey = String(doc.OnboardingDocumentId);
                                            const docState = documentsForm?.[docKey] || {};
                                            const fileLabel = docState.File?.name || (doc.UploadUrl ? "Uploaded file" : "No File Chosen");

                                            return (
                                                <div className="col-md-12 col-12" key={doc.OnboardingDocumentId}>
                                                    <label className="form-label">
                                                        {doc.DocumentName}
                                                        {doc.IsRequired ? <span className="text-danger mr-2">*</span> : ""}
                                                    </label>
                                                    <div className="row g-3">
                                                        <div className="col-md-6 col-12">
                                                            <div className="form-floating">
                                                                <input
                                                                    type="text"
                                                                    className="form-control"
                                                                    value={docState.DocumentNumber || ""}
                                                                    onChange={(e) => handleDocumentNumberChange(doc.OnboardingDocumentId, e.target.value)}
                                                                    placeholder={`Enter ${doc.DocumentName}`}
                                                                />
                                                                <label>{doc.DocumentName} Number</label>
                                                            </div>
                                                            {errors[`${docKey}-number`] && (
                                                                <div className="text-danger small">{errors[`${docKey}-number`]}</div>
                                                            )}
                                                            {!!doc.FacilityNotes && (
                                                                <small className="text-muted d-block mt-1">{doc.FacilityNotes}</small>
                                                            )}
                                                        </div>

                                                        <div className="col-md-6 col-12">
                                                            <div className="d-flex align-items-center gap-2 ssn-upload-row">
                                                                <div className="upload-card ssn-upload-card">
                                                                    <div className="file-upload mb-0">
                                                                        <div className="file-select">
                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-secondary rounded-1"
                                                                            >
                                                                                Choose File
                                                                            </button>
                                                                            <div className="file-select-name">{fileLabel}</div>
                                                                            <input
                                                                                type="file"
                                                                                className="profileimg"
                                                                                onChange={(e) => handleDocumentFileChange(doc.OnboardingDocumentId, e.target.files?.[0] || null)}
                                                                                accept="image/png, image/gif, image/jpeg, application/pdf, .doc, .docx"
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                {doc.UploadUrl && (
                                                                    <div className="icon-button download-icon-theme">
                                                                        <a
                                                                            href={doc.UploadUrl}
                                                                            target="_blank"
                                                                            rel="noreferrer"
                                                                            className="btn btn-link p-0 text-white"
                                                                        >
                                                                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                                                                                fill="currentColor" className="bi bi-download" viewBox="0 0 16 16">
                                                                                <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5" />
                                                                                <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708z" />
                                                                            </svg>
                                                                        </a>
                                                                    </div>
                                                                )}
                                                            </div>
                                                            {errors[`${docKey}-file`] && (
                                                                <div className="text-danger small">{errors[`${docKey}-file`]}</div>
                                                            )}
                                                        </div>
                                                    </div>

                                                    <div className="col-12">
                                                        <hr className="my-4" />
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </form> :
                                    <>
                                        <div className="onbset-empty-state-onboard">
                                            {/* <i className="mdi mdi-file-document-outline" /> */}
                                            <h5>Documents to be uploaded not configured.</h5>
                                        </div>
                                    </>
                                }
                                {(onboardingList?.Documents?.length > 0 && anythingChange) ?
                                    <div className="d-flex justify-content-end mt-4 container-padding-fix">
                                        <div className="col-auto me-2">
                                            {/* <button
                                            className="btn btn-primary-1"
                                            type="button"
                                            onClick={cancelForm}
                                        >
                                            CANCEL
                                        </button> */}
                                        </div>
                                        <div className="col-auto">
                                            <button
                                                className="btn btn-primary-2 bg-dark"
                                                type="submit"
                                                disabled={submitLoading}
                                                onClick={handleSubmit}
                                            >
                                                {submitLoading ? (
                                                    <>
                                                        <Spinner size="sm" className="me-2" /> Saving...
                                                    </>
                                                ) : (
                                                    "SAVE"
                                                )}
                                            </button>
                                        </div>
                                    </div> : null
                                }
                            </>
                        )}

                        <hr />
                        {/* <div className="row justify-content-center">
                    <div className="col-auto">
                        <button className="btn btn-primary-1">Not Interested Anymore</button>
                    </div>
                </div> */}
                        {/* <NotInterestedCommonButton /> */}

                        <CandidateTrackApplication offcanvasRef={offcanvasRef} offcanvasInstanceRef={offcanvasInstanceRef} />
                    </div>
                )}
        </>
    );
};

export default CandidateOnboardingSection;
